import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Link } from "react-router-dom";
import {
  Sparkles,
  AlertTriangle,
  FileWarning,
  Calendar,
  ChevronRight,
  RefreshCw,
  TrendingUp,
  Shield,
  CheckCircle2,
  Clock,
  Target,
  Zap,
} from "lucide-react";

export default function AIInsightsPanel() {
  const { api, user } = useAuth();
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchInsights = async (showRefreshState = false) => {
    if (showRefreshState) setRefreshing(true);
    else setLoading(true);
    
    try {
      const response = await api.post("/ai-assistant/insights-panel", {
        org_id: user?.org_id,
        user_role: user?.role
      });
      if (response.data?.success) {
        setInsights(response.data.data);
      }
    } catch (error) {
      console.error("Failed to fetch AI insights:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (user?.org_id) {
      fetchInsights();
    }
  }, [user?.org_id]);

  const getHealthColor = (score) => {
    if (score >= 80) return "text-emerald-600";
    if (score >= 60) return "text-amber-600";
    return "text-red-600";
  };

  const getHealthBg = (score) => {
    if (score >= 80) return "bg-emerald-100";
    if (score >= 60) return "bg-amber-100";
    return "bg-red-100";
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high": return "bg-red-100 text-red-700 border-red-200";
      case "medium": return "bg-amber-100 text-amber-700 border-amber-200";
      default: return "bg-slate-100 text-slate-700 border-slate-200";
    }
  };

  if (loading) {
    return (
      <Card className="border-sky-200 bg-gradient-to-br from-indigo-50 to-white">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Sparkles className="w-5 h-5 text-sky-700" />
            AI Insights
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin text-sky-700" />
            <span className="ml-2 text-slate-600">Analyzing your BCM program...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-sky-200 bg-gradient-to-br from-indigo-50 to-white">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Sparkles className="w-5 h-5 text-sky-700" />
            AI Insights
          </CardTitle>
          <div className="flex items-center gap-2">
            {/* Health Score Badge */}
            <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full ${getHealthBg(insights?.health_score || 0)}`}>
              <Target className={`w-4 h-4 ${getHealthColor(insights?.health_score || 0)}`} />
              <span className={`text-sm font-bold ${getHealthColor(insights?.health_score || 0)}`}>
                {insights?.health_score || 0}%
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => fetchInsights(true)}
              disabled={refreshing}
              className="h-8 w-8 p-0"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Policy Gaps */}
        {insights?.policy_gaps?.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <FileWarning className="w-4 h-4 text-amber-600" />
              Policy Gaps
            </h4>
            <div className="space-y-1.5">
              {insights.policy_gaps.slice(0, 3).map((gap, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
                  <span className="flex-1 text-amber-800">{gap.action}</span>
                  <Badge variant="outline" className={getPriorityColor(gap.severity)}>
                    {gap.severity}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Missing Data */}
        {insights?.missing_data?.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Shield className="w-4 h-4 text-red-600" />
              Missing Departmental Data
            </h4>
            <div className="space-y-1.5">
              {insights.missing_data.slice(0, 3).map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  <AlertTriangle className="w-3.5 h-3.5 text-red-600 flex-shrink-0" />
                  <span className="flex-1 text-red-800">{item.action}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Upcoming Milestones */}
        {insights?.upcoming_milestones?.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-blue-600" />
              Next 30 Days
            </h4>
            <div className="space-y-1.5">
              {insights.upcoming_milestones.slice(0, 3).map((milestone, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                  <Clock className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                  <span className="flex-1 text-blue-800">
                    <span className="font-medium">{milestone.name}</span>
                    <span className="text-blue-600 ml-1">({milestone.type})</span>
                  </span>
                  <Badge variant="outline" className={getPriorityColor(milestone.priority)}>
                    {milestone.days_until}d
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quick Actions */}
        {insights?.quick_actions?.length > 0 && (
          <div className="space-y-2 pt-2 border-t">
            <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Zap className="w-4 h-4 text-emerald-600" />
              Quick Actions
            </h4>
            <div className="grid gap-2">
              {insights.quick_actions.map((action, idx) => (
                <Link 
                  key={idx} 
                  to={action.link}
                  className="flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-lg hover:border-sky-300 hover:bg-sky-50 transition-colors group"
                >
                  <div className={`p-2 rounded-lg ${action.priority === 'high' ? 'bg-red-100' : 'bg-emerald-100'}`}>
                    <TrendingUp className={`w-4 h-4 ${action.priority === 'high' ? 'text-red-600' : 'text-emerald-600'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900">{action.title}</p>
                    <p className="text-xs text-slate-500 truncate">{action.description}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-sky-700 transition-colors" />
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {!insights?.policy_gaps?.length && 
         !insights?.missing_data?.length && 
         !insights?.upcoming_milestones?.length && (
          <div className="text-center py-6">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-2" />
            <p className="text-sm font-medium text-slate-700">Your BCM program is in great shape!</p>
            <p className="text-xs text-slate-500 mt-1">No critical gaps or upcoming deadlines detected.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
